import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIonToast } from '@ionic/react';
import { lastValueFrom } from 'rxjs';

import { ICasinoSchema } from 'appRedux/models/casinoModels';
import { IState } from 'appRedux/createStore';
import LinkPlayerCardModal from './LinkPlayerCardModal';
import { AuthService, ICasinoLinkedAccount, ILinkedAccounts } from 'services/auth';
import { ReduxCommandCreator } from 'appRedux/actions';
import { CasinoReduxCommandCreator } from 'appRedux/actions/casinoCommandCreator';
import { AUTH_STATE } from 'appRedux/types/reduxTypes';
import {
  bustLinkedAccountsCache,
  linkedAccountsBucket,
  playerScopedKey,
  sharedPlayerScopedGet,
} from 'lib/playerScopedListCache';

interface LinkPlayerWrapperProps {
  IsShowMaybeLater?: boolean;
}

export const LinkPlayerWrapper: React.FC<LinkPlayerWrapperProps> = ({
  IsShowMaybeLater = true,
}) => {
  const [visible, setVisible] = useState(true);
  const [presentToast] = useIonToast();
  const casinoSchema = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema
  ) as ICasinoSchema;
  const dispatch = useDispatch();
  const auth = new AuthService();
  const commands = ReduxCommandCreator(dispatch);
  const casinoCommands = CasinoReduxCommandCreator(dispatch);
  const userId = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.appUserPk
  ) as string;
  const casinoId = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.kpCasinoPk
  ) as number;

  const handleSuccessResponse = async () => {
    commands.SetAuthState(AUTH_STATE.AUTHENTICATED);
    try {
      if (userId) {
        bustLinkedAccountsCache(userId);
        const data = (await lastValueFrom(
          sharedPlayerScopedGet(linkedAccountsBucket, playerScopedKey(userId), () =>
            auth.GetLinkedAccounts(userId)
          )
        )) as ILinkedAccounts;
        if (data) {
          const linkedAccount = data.user.linkedAccounts as ICasinoLinkedAccount[];
          const casinoItem = linkedAccount?.find((item) => item.casinoId === casinoId);
          const isPlayerLinked = Boolean(casinoItem);
          const linkedCasinoId = casinoItem ? casinoItem.casinoId : null;
          casinoCommands.SetLinkedCasinos({
            casinoLinkedAccounts: linkedAccount,
            isPlayerLinked,
            linkedCasinoId,
          });
        }
      }
    } catch {
      void presentToast({
        position: 'top',
        duration: 5000,
        color: 'danger',
        message: 'Failed to retrieve linked accounts.',
      });
    }
  };

  if (!visible || !casinoSchema) return null;

  return (
    <LinkPlayerCardModal
      casinoInfo={casinoSchema}
      IsShowMaybeLater={IsShowMaybeLater}
      onSubmitSuccess={() => {
        setVisible(false);
        void handleSuccessResponse();
      }}
      onCancel={() => setVisible(false)}
    />
  );
};

export default LinkPlayerWrapper;
